package pl.nickers.match3

import kotlin.math.PI
import kotlin.math.sin

/**
 * Identifies every distinct in-game or UI sound that can be triggered.
 *
 * [placeholderFreqHz] is the sine-wave frequency (Hz) played by each platform
 * when the real audio asset file is absent.  Every event has a distinct pitch so
 * developers can confirm that the correct game event is firing even before real
 * audio files are added.
 */
enum class SoundEvent(val placeholderFreqHz: Int) {
    /** A valid player swap started (jellies are animating toward each other). */
    SWAP_VALID(440),        // A4

    /** An invalid swap is being returned (no match was produced). */
    SWAP_INVALID(220),      // A3 — lower = "no"

    /** A normal jelly match (3-in-a-row / 2×2) was detected and cleared. */
    MATCH_DETECTED(523),    // C5

    /** A bomb's 3×3 explosion resolved. */
    BOMB_EXPLODE(110),      // A2 — deep thud

    /** A laser fired across a row or column. */
    LASER_FIRE(880),        // A5 — high zap

    /** A colourful swirl match cleared all jellies of the matched colour. */
    SWIRL_CLEAR(1046),      // C6 — sparkle

    /** Jellies started falling due to gravity. */
    JELLIES_FALL(330),      // E4

    /** No valid move remains — the game is over. */
    GAME_OVER(165),         // E3 — low, sad

    /** A UI button was tapped. */
    UI_CLICK(660),          // E5
}

/**
 * Generates a minimal 16-bit mono PCM WAV in memory at [freqHz] Hz for [durationMs] ms.
 *
 * The output includes a standard 44-byte RIFF/WAVE/fmt /data header so it can be
 * loaded directly by any WAV parser (SoundPool, AVAudioPlayer, javax.sound.sampled).
 * A short fade-out envelope avoids click artifacts at the end.
 *
 * This is a pure-Kotlin utility; it is used by each platform `actual` implementation
 * as a placeholder when the real audio asset is not found.
 */
internal fun generatePlaceholderWavBytes(freqHz: Int, durationMs: Int = 150): ByteArray {
    val sampleRate = 22050
    val numSamples = sampleRate * durationMs / 1000
    val dataSize = numSamples * 2  // 16-bit = 2 bytes per sample
    val bytes = ByteArray(44 + dataSize)
    var pos = 0

    fun str(s: String) = s.forEach { bytes[pos++] = it.code.toByte() }
    fun i32(v: Int) { repeat(4) { bytes[pos++] = ((v shr (it * 8)) and 0xFF).toByte() } }
    fun i16(v: Int) { bytes[pos++] = (v and 0xFF).toByte(); bytes[pos++] = ((v shr 8) and 0xFF).toByte() }

    str("RIFF"); i32(36 + dataSize); str("WAVE")
    str("fmt "); i32(16)
    i16(1); i16(1)                        // PCM, mono
    i32(sampleRate); i32(sampleRate * 2)  // sample rate, byte rate
    i16(2); i16(16)                       // block align, bits/sample
    str("data"); i32(dataSize)

    val fadeStart = (numSamples * 0.75).toInt()
    for (i in 0 until numSamples) {
        val envelope = if (i >= fadeStart) (numSamples - i).toDouble() / (numSamples - fadeStart) else 1.0
        val sample = (sin(2.0 * PI * freqHz * i / sampleRate) * Short.MAX_VALUE * 0.35 * envelope).toInt()
        i16(sample.coerceIn(Short.MIN_VALUE.toInt(), Short.MAX_VALUE.toInt()))
    }
    return bytes
}

/**
 * Cross-platform audio facade.
 *
 * Lifecycle:
 *   1. Call [initialize] once after construction (loads assets, opens audio streams).
 *   2. Call [startMusic] when the first user gesture arrives (respects browser autoplay policy).
 *   3. Call [release] in ViewModel.onCleared() / Activity.onDestroy().
 *
 * **Placeholder behaviour**: when a real audio asset file is not found, each platform
 * implementation synthesises a short sine-wave beep at [SoundEvent.placeholderFreqHz].
 * This lets developers confirm that the correct game event is firing before real audio
 * files are added.  See README.md for the expected asset file locations.
 *
 * Asset naming convention expected by platform implementations:
 *   SFX  : "audio/sfx_{SoundEvent.name.lowercase()}.{ext}"
 *   Music: "audio/bgm_game.{ext}"
 */
expect class AudioPlayer() {
    /** Load audio assets and open platform streams. Fire-and-forget; safe to call once. */
    fun initialize()

    /** Release all platform audio resources. Call from ViewModel.onCleared(). */
    fun release()

    // ---- SFX ----------------------------------------------------------------

    /** Play a one-shot sound effect for the given game event. */
    fun playSfx(event: SoundEvent)

    /** Set master SFX volume [0.0, 1.0]. Persists across playSfx calls. */
    fun setSfxVolume(volume: Float)

    /** Mute/unmute SFX without losing the stored volume. */
    fun setSfxMuted(muted: Boolean)

    // ---- Background music ---------------------------------------------------

    /**
     * Start (or resume) the background music track, looping indefinitely.
     * Call on the first user gesture to satisfy browser autoplay policies.
     */
    fun startMusic()

    /** Pause the background music. */
    fun stopMusic()

    /** Set BGM volume [0.0, 1.0]. */
    fun setMusicVolume(volume: Float)

    /** Mute/unmute BGM without losing the stored volume. */
    fun setMusicMuted(muted: Boolean)
}
